import React, { useState, useEffect } from 'react';
import { Ship } from '../../interfaces/types';
import { shipService } from '../../services/shipService';
import { FaEdit, FaCheck, FaTimes, FaTools } from 'react-icons/fa';
import { showToast } from '../common/ToastContainer';
import './ShipGarageList.css';
import { useUser } from '../../hooks/useUser';

interface ShipGarageListProps {
  ships: Ship[];
  totalShips?: number;
  onShipSelect: (ship: Ship) => void;
  onRefresh?: () => void;
  isCompareMode?: boolean;
  onCompareSelect?: (ship: Ship) => void;
  selectedForCompare?: string[];
}

export const ShipGarageList: React.FC<ShipGarageListProps> = ({ 
  ships, 
  totalShips,
  onShipSelect,
  onRefresh,
  isCompareMode = false,
  onCompareSelect,
  selectedForCompare = []
}) => {
  const { username } = useUser();
  const [editingShip, setEditingShip] = useState<string | number | null>(null);
  const [editingNick, setEditingNick] = useState<string>('');
  const [localShips, setLocalShips] = useState<Ship[]>(ships);

  useEffect(() => {
    setLocalShips(ships);
  }, [ships]);

  const handleEditClick = (e: React.MouseEvent, ship: Ship) => {
    e.stopPropagation();
    setEditingShip(ship.id);
    setEditingNick(ship.nick || ship.name);
  };

  const handleSaveNick = async (e: React.MouseEvent, shipId: number, nick: string) => {
    e.stopPropagation();
    try {
      if (!username) {
        return;
      }

      if (!nick.trim()) {
        return;
      }

      await shipService.updateShipNick(shipId, nick);
      
      setLocalShips(currentShips => 
        currentShips.map(s => 
          s.id === shipId ? { ...s, nick: nick } : s
        )
      );
      
      showToast(
        `✅ El nick "${nick}" ha sido aplicado a la nave ${ships.find(s => s.id === shipId)?.name}`,
        'success'
      );
      
      setEditingShip(null);
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error al actualizar nick:', error);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingShip(null);
    setEditingNick('');
  };

  return (
    <div className="garage-list">
      <h2>Mi Garage ({totalShips || ships.length} naves{totalShips && totalShips !== ships.length ? ` - Mostrando ${ships.length}` : ''})</h2>
      <div className="garage-grid">
        {localShips.map((ship) => (
          <div 
            key={ship.id} 
            className={`garage-card ${editingShip === ship.id ? 'editing' : ''} ${
              isCompareMode ? 'compare-mode' : ''
            } ${selectedForCompare.includes(ship.name) ? 'selected-for-compare' : ''}`}
          >
            <div className="garage-card__image">
              <button 
                className="repair-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShipSelect(ship);
                }}
                title="Reparar/Modificar nave"
              >
                <FaTools />
              </button>

              {isCompareMode && (
                <div 
                  className={`ship-card__compare ${selectedForCompare.includes(ship.name) ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompareSelect?.(ship);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedForCompare.includes(ship.name)}
                    onChange={() => {}}
                    id={`compare-${ship.id}`}
                  />
                  <label htmlFor={`compare-${ship.id}`}>Comparar</label>
                </div>
              )}

              <img 
                src={ship.imagen} 
                alt={ship.name}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = '/media/naves/not_found.png';
                }}
              />
            </div>
            <div className="garage-card__content">
              <div className="garage-card__header">
                <h3>{ship.name}</h3>
                {editingShip === ship.id ? (
                  <div className="nick-edit">
                    <input
                      type="text"
                      value={editingNick}
                      onChange={(e) => setEditingNick(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    <button onClick={(e) => handleSaveNick(e, ship.id, editingNick)}>
                      <FaCheck />
                    </button>
                    <button onClick={handleCancelEdit}>
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="nick-display">
                    <span>{ship.nick || ship.name}</span>
                    <button onClick={(e) => handleEditClick(e, ship)}>
                      <FaEdit />
                    </button>
                  </div>
                )}
              </div>
              <div className="garage-card__stats">
                <span>Estado: {ship.production_status}</span>
                <span>Tripulación: {ship.min_crew}-{ship.max_crew}</span>
                <span>Carga: {ship.cargo_capacity}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};